<!-- @format -->
<template>
    <div class="psurvey-widget">
        <loading-card :is-loading="isLoading || isSaving" />
        <div class="widget-contents-container">
            <div class="widget-header">
                <div class="header-text">
                    <h3>{{ widgetTitle }}</h3>
                </div>
                <div class="svg-container widget-close-button" @click="closeWidget">
                    <widget-close-svg />
                </div>
            </div>
            <div class="content-wrapper">
                <div v-if="!isLoading" class="fields-container">
                    <app-input
                        v-model="partnerSurveyConfig.name"
                        class="name-container"
                        :disabled="isLoading"
                        label="Survey Name (For internal purposes)"
                        placeholder=" "
                        :is-valid="isNameValid"
                        error="This field is required"
                        @blur="validateName"
                    />
                    <app-input
                        v-model="partnerSurveyConfig.title"
                        :disabled="isLoading"
                        label="Survey Title (The title your partners will see)"
                        placeholder=" "
                        :is-valid="isTitleValid"
                        error="This field is required"
                        @blur="validateTitle"
                    />
                    <div class="switch-container">
                        <app-switch v-model="partnerSurveyConfig.isProgressRequired" size="small" />
                        <p>This survey contributes to your partner's setup progress</p>
                        <app-tooltip-icon
                            :description="isProgressRequiredDescription"
                            tooltip-classes="vue-tooltip-theme partner-survey-tooltip"
                        >
                            <img
                                slot="tooltipIcon"
                                :src="require('@/assets/images/SVG/black-info-icon.svg')"
                            />
                        </app-tooltip-icon>
                    </div>
                    <hr />
                    <div class="email-list-container">
                        <label>Who Receives the Survey Submission Notification?</label>
                        <div
                            v-for="(recipient, index) in partnerSurveyConfig.emails"
                            :key="recipient.id"
                        >
                            <app-input
                                v-model="recipient.email"
                                placeholder=" "
                                :is-valid="isEmailValid(index)"
                                error="Please enter a valid email address"
                                :disabled="isLoading"
                                @blur="validateEmail(index)"
                            >
                                <div
                                    slot="inputIcon"
                                    class="icon"
                                    @click="handleRemoveRecipient(recipient.id)"
                                >
                                    <s3-close-svg />
                                </div>
                            </app-input>
                        </div>
                        <app-button class="add-option-btn" @click="handleAddNewRecipient">
                            <o6-add-svg color="#ffff" />
                        </app-button>
                    </div>
                    <app-input
                        v-model="partnerSurveyConfig.emailSubject"
                        label="Email Subject Line"
                        placeholder=" "
                    />
                </div>
            </div>
            <div class="widget-footer">
                <app-button class="cancel-btn transparent-btn" @click="closeWidget">
                    Cancel
                </app-button>
                <div class="next-btns-container">
                    <app-button :disabled="isNextDisabled" class="next-btn" @click="savePartnerSurvey('close')">
                        Save and Close
                    </app-button>
                    <app-button :disabled="isNextDisabled" class="next-btn" @click="savePartnerSurvey('continue')">
                        Save and Continue
                        <m6-next-svg />
                    </app-button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import AppPopup from '@/components/popups/app-popup';
import EventBus from '@/scripts/event-bus.js';
import { PartnerSurveyService } from '@/scripts/services/partner-survey/partner-survey-service';
import { required, email } from 'vuelidate/lib/validators';

const AppButton = () => import('@/components/common/app-button.vue');
const AppInput = () => import('@/components/common/app-input');
const AppSwitch = () => import('@/components/common/app-switch');
const AppTooltipIcon = () => import('@/components/common/app-tooltip-icon');
const LoadingCard = () => import('@/components/common/loading-card');
const M6NextSvg = () => import('@/components/svg/grid/m6-next-svg');
const O6AddSvg = () => import('@/components/svg/grid/o6-add-svg');
const S3CloseSvg = () => import('@/components/svg/grid/s3-close-svg');
const WidgetCloseSvg = () => import('@/components/svg/quick-add/widget-close-svg.vue');

export default {
    name: 'psurvey-widget',

    components: {
        'app-button': AppButton,
        'app-input': AppInput,
        'app-switch': AppSwitch,
        'app-tooltip-icon': AppTooltipIcon,
        'loading-card': LoadingCard,
        'm6-next-svg': M6NextSvg,
        'o6-add-svg': O6AddSvg,
        's3-close-svg': S3CloseSvg,
        'widget-close-svg': WidgetCloseSvg,
    },

    props: {
        widget: {
            type: Object,
            required: true,
        },
    },

    data() {
        return {
            isLoading: false,

            isSaving: false,

            partnerSurveyConfig: {
                name: '',
                title: '',
                isProgressRequired: false,
                isPublished: false,
                emailSubject: 'You have a new survey submission',
                emails: [
                    {
                        id: this.$helpers.createId('email'),
                        email: '',
                    },
                ],
            },

            isProgressRequiredDescription:
                'Enabling this field makes completing the survey required for achieving a 100% setup progress on the Partners page.',
        };
    },

    computed: {
        baseUrl() {
            return this.$store.getters.baseUrl;
        },

        accountId() {
            return this.widget.options.accountId;
        },

        partnerSurveyId() {
            const { options } = this.widget;
            return options ? options.partnerSurveyId : null;
        },

        isEditMode() {
            return !!this.partnerSurveyId;
        },

        widgetTitle() {
            return this.isEditMode ? 'Edit Survey' : 'Create a New Survey';
        },

        emails() {
            return this.partnerSurveyConfig.emails.reduce((acc, { email }) => {
                if (email) {
                    acc.push({ Email: email });
                }
                return acc;
            }, []);
        },

        isNextDisabled() {
            return this.isLoading || this.isSaving || (this.$v.$anyDirty && this.$v.$anyError);
        },

        isNameValid() {
            return !this.$v.partnerSurveyConfig.name.$error;
        },

        isTitleValid() {
            return !this.$v.partnerSurveyConfig.title.$error;
        },
    },

    created() {
        if (this.isEditMode) {
            this.getEditPartnerSurveyData();
        }
    },

    validations: {
        partnerSurveyConfig: {
            name: {
                required,
            },

            title: {
                required,
            },

            emails: {
                $each: {
                    $trackBy: 'id',
                    email: {
                        email,
                    },
                },
            },
        },
    },

    methods: {
        closeWidget() {
            EventBus.$emit('close-widget', this.widget);
        },

        isEmailValid(index) {
            return !this.$v.partnerSurveyConfig.emails.$each[index].$error;
        },

        validateName() {
            this.$v.partnerSurveyConfig.name.$touch();
        },

        validateTitle() {
            this.$v.partnerSurveyConfig.title.$touch();
        },

        validateEmail(index) {
            this.$v.partnerSurveyConfig.emails.$each[index].email.$touch();
        },

        handleRemoveRecipient(id) {
            this.partnerSurveyConfig.emails = this.partnerSurveyConfig.emails.filter(
                (option) => option.id !== id,
            );
        },

        handleAddNewRecipient() {
            this.partnerSurveyConfig.emails.push({
                id: this.$helpers.createId('recipient'),
                email: '',
            });
        },

        savePartnerSurvey(action) {
            this.validateName();
            this.validateTitle();

            if (!this.isNameValid || !this.isTitleValid) return;

            if (this.isEditMode) {
                if (this.partnerSurveyConfig.isPublished && action === 'continue') {
                    return this.confirmUpdate(() => this.updatePartnerSurvey(action));
                }

                return this.updatePartnerSurvey(action);
            } else {
                return this.createPartnerSurvey(action);
            }
        },

        createPartnerSurvey(action) {
            this.isSaving = true;
            this.postPartnerSurvey()
                .then(({ Id }) => {
                    return this.postPartnerSurveyRecipients(Id);
                })
                .then((id) => {
                    this.$helpers.showSuccessNotification('Partner survey created successfully.');
                    this.closeWidget();
                    
                    if (action === 'continue') {
                        this.$router.push(`${this.baseUrl}/PartnerSurveys/${id}/Design`);
                    }
                    else {
                        EventBus.$emit('partner-survey:update');
                    }
                })
                .catch(() => {
                    this.closeWidget();
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        postPartnerSurvey() {
            const payload = {
                Name: this.partnerSurveyConfig.name,
                Title: this.partnerSurveyConfig.title,
                ContributeToSetupProgress: this.partnerSurveyConfig.isProgressRequired,
                EmailSubjectLine: this.partnerSurveyConfig.emailSubject,
            };

            return new Promise((resolve, reject) => {
                PartnerSurveyService.post({ accountId: this.accountId, payload })
                    .then(({ data: { data } }) => {
                        resolve(data);
                    })
                    .catch(() => {
                        this.$helpers.showFailNotification(
                            'An error occurred while creating your partner survey.',
                        );
                        reject();
                    });
            });
        },

        postPartnerSurveyRecipients(id) {
            return new Promise((resolve, reject) => {
                PartnerSurveyService.postRecipients({
                    id,
                    accountId: this.accountId,
                    payload: {
                        recipients: this.emails,
                    },
                })
                    .then(() => {
                        resolve(id);
                    })
                    .catch(() => {
                        this.$helpers.showFailNotification(
                            'An error occurred while creating your partner survey recipients.',
                        );
                        reject();
                    });
            });
        },

        confirmUpdate(callback) {
            const actions = {
                confirm: {
                    label: 'Make Edits',
                    action: callback,
                    type: 'removeNotIcon',
                },

                cancel: {
                    label: 'Cancel',
                },
            };

            this.$modal.show(
                AppPopup,
                {
                    type: 'alert-popup',
                    title: 'Do you want to edit this survey?',
                    content:
                        '<p>Editing a published survey will change any completed surveys to <b>draft.</b></p>',
                    actions,
                    confirmText: 'Make Edits',
                },
                {
                    height: this.$helpers.setPopupHeight(),
                    width: this.$helpers.setPopupWidth(),
                },
            );
        },

        updatePartnerSurvey(action) {
            this.isSaving = true;
            this.patchPartnerSurvey()
                .then((id) => {
                    return this.patchPartnerSurveyRecipients(id);
                })
                .then((id) => {
                    this.closeWidget();
                    if (action === 'continue') {
                        this.$router.push(`${this.baseUrl}/PartnerSurveys/${id}/Design`);
                    }
                    else {
                        EventBus.$emit('partner-survey:update');
                    }
                })
                .finally(() => {
                    this.isSaving = false;
                });
        },

        patchPartnerSurvey() {
            const payload = {
                Name: this.partnerSurveyConfig.name,
                Title: this.partnerSurveyConfig.title,
                ContributeToSetupProgress: this.partnerSurveyConfig.isProgressRequired,
                EmailSubjectLine: this.partnerSurveyConfig.emailSubject,
            };

            return new Promise((resolve, reject) => {
                PartnerSurveyService.patch({
                    id: this.partnerSurveyId,
                    accountId: this.accountId,
                    payload,
                })
                    .then(({ data: { data } }) => {
                        resolve(data.Id);
                    })
                    .catch(() => {
                        this.$helpers.showFailNotification('Failed to update the partner survey.');
                        reject();
                    });
            });
        },

        patchPartnerSurveyRecipients(id) {
            return new Promise((resolve, reject) => {
                PartnerSurveyService.patchRecipients({
                    id,
                    accountId: this.accountId,
                    payload: {
                        recipients: this.emails,
                    },
                })
                    .then(() => {
                        resolve(id);
                    })
                    .catch(() => {
                        this.$helpers.showFailNotification(
                            'An error occurred while updating your partner survey recipients.',
                        );
                        reject();
                    });
            });
        },

        getEditPartnerSurveyData() {
            this.isLoading = true;
            return PartnerSurveyService.getById({
                id: this.partnerSurveyId,
                accountId: this.accountId,
            })
                .then(({ data: { data } }) => {
                    const [survey] = data;
                    this.partnerSurveyConfig = {
                        ...this.partnerSurveyConfig,
                        name: survey.Name,
                        title: survey.Title,
                        isProgressRequired: survey.ContributeToSetupProgress,
                        isPublished: survey.IsPublished,
                        emailSubject: survey.EmailSubjectLine,
                    };
                    return survey.Id;
                })
                .then((id) => {
                    return PartnerSurveyService.getRecipients({ id, accountId: this.accountId });
                })
                .then(({ data: { data } }) => {
                    if (data && data.length > 0) {
                        this.partnerSurveyConfig.emails = data.map(({ Email, Id }) => ({
                            email: Email,
                            id: Id,
                        }));
                    }
                })
                .catch(() => {
                    this.$helpers.showFailNotification(
                        'An error occurred while getting your partner survey.',
                    );
                    this.closeWidget();
                })
                .finally(() => {
                    this.isLoading = false;
                });
        },
    },
};
</script>

<style>
.psurvey-widget .widget-contents-container .content-wrapper {
    padding: 27px 24px 24px;
}

.psurvey-widget .widget-contents-container .fields-container {
    display: flex;
    flex-direction: column;
    gap: 24px;
}

.psurvey-widget .content-wrapper .app-input input {
    height: 40px;
}

.psurvey-widget .content-wrapper .textarea-container textarea {
    resize: none;
}

.psurvey-widget .content-wrapper .textarea-container textarea::-webkit-scrollbar {
    display: none;
}

.psurvey-widget .app-input label,
.psurvey-widget .app-textarea label {
    font-weight: bold;
    margin-bottom: 8px;
}

.psurvey-widget .name-container .error-message {
    margin-bottom: 0;
}

.psurvey-widget .switch-container {
    display: flex;
    flex-direction: row;
    gap: 8px;
}

.psurvey-widget .switch-container .app-switch {
    width: fit-content;
}

.psurvey-widget .switch-container p {
    color: #382153;
    font: 500 16px/24px Roboto;
    margin: 0;
}

.psurvey-widget .email-list-container .app-input {
    margin-bottom: 16px;
}

.psurvey-widget .email-list-container .app-input .icon-container {
    top: 10px;
    right: 8px;
}

.psurvey-widget .email-list-container .app-input .icon-container:hover {
    cursor: pointer;
}

.psurvey-widget .email-list-container .app-input .icon-container svg {
    height: 14px;
    width: 14px;
}

.psurvey-widget .error-message {
    display: block;
    color: #f30070;
    font: 500 12px 'Roboto';
    margin-top: 8px;
}

.psurvey-widget .next-btns-container {
    display: flex;
    flex-direction: row;
    gap: 16px;
}

.psurvey-widget .email-list-container .add-option-btn {
    background-color: #18aeff;
    height: 40px;
    width: 100%;
}

.psurvey-widget .email-list-container .add-option-btn svg {
    height: 18px;
    width: 18px;
}

.psurvey-widget .email-list-container .add-option-btn svg path {
    fill: #ffffff;
}

.psurvey-widget .app-tooltip-icon {
    width: 16px;
}

.psurvey-widget hr {
    border-color: #d5d2d6;
    margin: 0;
}

.psurvey-widget .transparent-btn {
    background: transparent;
    border-radius: 20px;
    border: 1px solid #cac6cb;
    color: #382153;
}

.psurvey-widget .transparent-btn:hover {
    background: #dcdcdc;
}

.psurvey-widget .m6-next-svg {
    height: 12px;
    margin-left: 16px;
    width: 16px;
}

.psurvey-widget .m6-next-svg .a {
    fill: #fff;
}

.partner-survey-tooltip {
    max-width: 358px;
}
</style>
